import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        apiKey: String,
        lat: Number,
        lng: Number,
        radius: Number,
        editable: { type: Boolean, default: true }
    }

    static targets = ["map", "latInput", "lngInput", "radiusInput"]

    async connect() {
        const { setOptions, importLibrary } = await import("@googlemaps/js-api-loader")

        setOptions({
            apiKey: this.apiKeyValue,
            version: "weekly",
        })

        try {
            const { Map, Circle } = await importLibrary("maps")
            const { Marker } = await importLibrary("marker")
            this.initMap(Map, Marker, Circle)
        } catch (e) {
            console.error("Error loading Google Maps", e)
        }
    }

    // loadMaps helper is no longer needed with this pattern

    initMap(Map, Marker, Circle) {
        const defaultLocation = { lat: 25.2048, lng: 55.2708 } // Dubai
        const position = (this.hasLatValue && this.hasLngValue && this.latValue && this.lngValue)
            ? { lat: this.latValue, lng: this.lngValue }
            : defaultLocation

        this.map = new Map(this.mapTarget, {
            center: position,
            zoom: 12,
            mapId: "DEMO_MAP_ID", // Required for AdvancedMarkerElement if used, though Marker is standard
        })

        this.marker = new Marker({
            position: position,
            map: this.map,
            draggable: this.editableValue,
            title: "Location"
        })

        if (this.editableValue) {
            this.marker.addListener("dragend", (event) => {
                this.updateInputs(event.latLng.lat(), event.latLng.lng())
            })

            this.map.addListener("click", (event) => {
                this.marker.setPosition(event.latLng)
                this.updateInputs(event.latLng.lat(), event.latLng.lng())
            })
        }

        if (this.hasRadiusValue && this.radiusValue > 0) {
            this.initCircle(Circle)
        }
    }

    initCircle(Circle) {
        this.circle = new Circle({
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 0.35,
            map: this.map,
            center: this.marker.getPosition(),
            radius: this.radiusValue * 1000, // km to meters
            editable: this.editableValue
        })

        // Update circle when marker moves
        if (this.editableValue) {
            this.marker.addListener("drag", (event) => {
                this.circle.setCenter(event.latLng)
            })

            this.marker.addListener("dragend", (event) => {
                this.circle.setCenter(event.latLng)
            })

            this.circle.addListener("radius_changed", () => {
                const km = this.circle.getRadius() / 1000
                this.updateRadiusInput(km)
            })

            this.circle.addListener("center_changed", () => {
                // Optional: Update marker if circle center dragged separately? 
                // Typically Circle is bound to marker.
                const center = this.circle.getCenter()
                if (center && (center.lat() !== this.marker.getPosition().lat() || center.lng() !== this.marker.getPosition().lng())) {
                    this.marker.setPosition(center)
                    this.updateInputs(center.lat(), center.lng())
                }
            })
        }
    }

    updateInputs(lat, lng) {
        if (this.hasLatInputTarget) this.latInputTarget.value = lat
        if (this.hasLngInputTarget) this.lngInputTarget.value = lng
    }

    updateRadiusInput(km) {
        if (this.hasRadiusInputTarget) this.radiusInputTarget.value = km.toFixed(2)
    }

    radiusInputTargetConnected(element) {
        element.addEventListener("change", (e) => {
            const km = parseFloat(e.target.value)
            if (this.circle && !isNaN(km)) {
                this.circle.setRadius(km * 1000)
            }
        })
    }
}
